import { em, Group, Skeleton, Text } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { useInstantSearch } from 'react-instantsearch'
import { Box, Container, Flex, Grid } from 'components/Layout'
import { useUserAuth } from 'providers/userAuth'
import { COLOR_NAMES, SPACE } from 'Theme'
import { CurrentRefinements } from '../CurrentRefinements'
import { Filter } from '../Filter'
import { ProductHits } from '../ProductHits'
import { TypesenseMobileFilterAndSort } from '../TypesenseMobileFilterAndSort'
import { TypeSenseSortBy } from '../TypeSenseSortBy'

export const ScrollToTopOnSearchChange = () => {
  const { results } = useInstantSearch()
  useEffect(() => {
    if (results) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [results?.nbHits])

  return null
}

export const BasePageLayout = ({
  showParentLevel,
  rootPath,
  showResultsCount,
  defaultSortIndex,
  rootCategory,
}: {
  showParentLevel: boolean
  rootPath?: string
  showResultsCount?: boolean
  defaultSortIndex?: number
  rootCategory?: string
}) => {
  const isMobile = useMediaQuery(`(max-width: ${em(768)})`)
  const { user } = useUserAuth()
  const router = useRouter()
  const { results } = useInstantSearch()
  // const { status } = useInstantSearch()
  // const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      // Only scroll if query params changed, not path
      if (url.includes('?')) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  // problem with typesense multiple requests
  // useEffect(() => {
  //   let timeout
  //   if (status === 'loading' || status === 'stalled') {
  //     timeout = setTimeout(() => setIsLoading(true), 200)
  //   } else {
  //     clearTimeout(timeout)
  //     setIsLoading(false)
  //   }
  //   return () => clearTimeout(timeout)
  // }, [status])

  // console.log(isLoading)

  const isLoading = false

  return (
    <Box>
      <ScrollToTopOnSearchChange />
      <Container>
        <Group
          justify={isMobile ? 'flex-start' : 'space-between'}
          align="flex-start"
          py="1rem"
          wrap="nowrap"
          style={{ flexDirection: isMobile ? 'column-reverse' : 'row' }}
        >
          <Flex alignItems="center" gap={16} minHeight={36}>
            {typeof results?.nbHits === 'number' &&
              !isLoading &&
              results.nbHits > 0 &&
              showResultsCount && (
                <Text fz={14} fw={500}>
                  {results.nbHits.toLocaleString('en-US')} Products
                </Text>
              )}
            <Box flex={1} minWidth={0} maxWidth="100%">
              {!isLoading && <CurrentRefinements />}
            </Box>
          </Flex>

          <Box flex="none" display={{ MOBILE: 'none', TABLET: 'block' }}>
            {isLoading ? (
              <Skeleton width="160px" height="36px" radius={10} animate />
            ) : (
              <TypeSenseSortBy defaultIndex={defaultSortIndex} />
            )}
          </Box>

          <Box display={{ MOBILE: 'block', TABLET: 'none' }} width="100%">
            <TypesenseMobileFilterAndSort>
              <TypeSenseSortBy popover={false} />
              <Filter
                isTradeCapable={user?.isTradeCapable}
                showParentLevel={showParentLevel}
                rootPath={rootPath}
                rootCategory={rootCategory}
              />
            </TypesenseMobileFilterAndSort>
          </Box>
        </Group>
      </Container>

      <Flex
        flex="1"
        flexDirection="column"
        mb={{ TABLET: SPACE.PX_40, MOBILE: SPACE.PX_5 }}
      >
        <Container>
          <Flex
            gap={{
              MOBILE: SPACE.PX_16,
              TABLET: SPACE.PX_32,
            }}
          >
            {/* Sidebar */}
            <Box
              width={{ MOBILE: '100%', TABLET: '25%' }}
              display={{ MOBILE: 'none', TABLET: 'block' }}
            >
              <Box
                borderBottom="1px solid"
                borderColor={COLOR_NAMES.JUPITER}
                position="sticky"
                top={SPACE.PX_100}
                overflow="auto"
                height="calc(100vh - 108px)"
                pr="16px"
              >
                {isLoading ? (
                  <>
                    <Skeleton height="63px" animate mb={1} />
                    <Skeleton height="63px" animate mb={1} />
                    <Skeleton height="63px" animate mb={1} />
                  </>
                ) : (
                  <Filter
                    isTradeCapable={user?.isTradeCapable}
                    showParentLevel={showParentLevel}
                    rootPath={rootPath}
                    rootCategory={rootCategory}
                  />
                )}
              </Box>
            </Box>

            {/* Main content */}
            <Box width={{ MOBILE: '100%', TABLET: '75%' }}>
              {isLoading ? (
                <Grid
                  gridTemplateColumns={{
                    MOBILE: '1fr 1fr',
                    TABLET: 'repeat(2, 1fr)',
                    DESKTOP: 'repeat(3, 1fr)',
                  }}
                  gap={{
                    MOBILE: SPACE.PX_16,
                    TABLET: SPACE.PX_32,
                  }}
                >
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} height="400px" animate mb={1} />
                  ))}
                </Grid>
              ) : (
                <ProductHits />
              )}
            </Box>
          </Flex>
        </Container>
      </Flex>
    </Box>
  )
}
